<script>

import * as turf from '@turf/turf'
import mapboxgl from 'mapbox-gl';
import {axios} from "../../mixins";

// Import plugins
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';

// Import styles
import 'mapbox-gl/dist/mapbox-gl.css'
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'

export default {
  name: 'MapBoxSelect',

  mixins: [axios],

  props: {
    token: {
      required: true,
      type: String,
    },
    esriToken: {
      required: true,
      type: String,
    },
    render: {
      required: false,
      default: true,
      type: Boolean,
    },
    popups: {
      required: false,
      type: Boolean,
      default: false,
    },
    mapId: {
      required: false,
      type: String,
      default: null,
    },
    digifarmToken: {
      required: true,
      type: String,
    },
    layers: {
      required: false,
      type: Object,
      default: null,
    },
    farmAddress: {
      required: false,
      type: String,
    },
    zipCode: {
      required: false,
      type: String,
    }
  },

  data() {
    return {
      mapStyle: 'https://basemapstyles-api.arcgis.com/arcgis/rest/services/styles/v2/styles/arcgis/imagery',
      map: null,
      geography: false,
      loaded: false,
      center: [-93.627594, 42.025494],
      identifier: 'map-' + Math.floor(10000 * Math.random()),
      fullBounds: new mapboxgl.LngLatBounds(),
      sourceId: null,
      boundaries: [],
      hoveredFeature: null,
      selected: [],
      listeners: false,
      showZoomTooltip: true,
    };
  },

  mounted() {
    this.renderMap()
  },

  methods: {
    /**
     * Initiates creating the map and loading the data.
     */
    async renderMap() {
      await this.createMap();
      this.addControls();

      this.map.on('load', function () {
        this.loaded = true
        let map = this.map;
        // map.addSource('naip-usgs',{
        //   'type':'raster',
        //   'tiles':[
        //     'https://imagery.nationalmap.gov:443/arcgis/services/USGSNAIPImagery/ImageServer/WMSServer?crs=EPSG:3857&transparency=false&request=GetMap%26version=1.3.0%26format=image/jpeg%26layers=USGSNAIPImagery:NaturalColor&bbox={bbox-epsg-3857}&width=' + map.getCanvas().width + '&height=' + map.getCanvas().height
        //   ],
        //   'tileSize': 256
        // });
        // map.removeLayer('satellite')
        // map.addLayer(
        //     {
        //       'id': 'satellite',
        //       'type': 'raster',
        //       'source': 'naip-usgs',
        //     },
        //     'tunnel-primary-secondary-tertiary-case' // Arrange our new layer beneath labels and above roads
        // );
        this.map.addLayer({
          'id': 'fieldBoundary',
          'type': 'fill',
          'source': {
            'type': 'geojson',
            'data': this.layers
          },
          'layout': {},
          'paint': {
            'fill-color': '#008ED3',
            'fill-opacity': 0.5
          }
        });
        this.map.resize()
        this.postMapRender()
      }.bind(this));
    },

    /**
     * Creates map with added plugins.
     */
    async createMap() {
      mapboxgl.accessToken = this.token;
      if (!localStorage.getItem('sessionEnd')) {
        localStorage.setItem('sessionEnd', 0)
      }

      if (localStorage.getItem('sessionEnd') < Date.now()) {
        const response = await this.get("https://basemapstyles-api.arcgis.com/arcgis/rest/services/styles/v2/sessions/start" +
            "?styleFamily=arcgis" +
            "&f=json" +
            "&token=" + this.esriToken)

        localStorage.setItem('sessionToken', response.data.sessionToken)
        localStorage.setItem('sessionEnd', response.data.endTime)
      }

      this.map = new mapboxgl.Map({
        container: this.mapId ?? this.identifier,
        style: this.mapStyle + "?token=" + localStorage.getItem('sessionToken'), // stylesheet location
        center: this.center,
        zoom: 3, // starting zoom
      });

      this.map.on('moveend', () => {
        if (this.map.getZoom() > 13) {
          this.getBoundaries()
          this.showZoomTooltip = false
        } else {
          this.showZoomTooltip = true
        }
      });
    },

    /**
     * Adds data to map and sets bounds.
     */
    postMapRender() {
      if (this.geography != null) {
        if (this.geography.type === 'FeatureCollection') {
          this.geography.features.forEach((element) => {
            this.addGeography(element);
          })
        }
      }
    },

    /**
     * Adds map controls
     */
    addControls() {
      /* Given a query in the form "lng, lat" or "lat, lng"
        * returns the matching geographic coordinate(s)
        * as search results in carmen geojson format,
        * https://github.com/mapbox/carmen/blob/master/carmen-geojson.md */
      const coordinatesGeocoder = function (query) {
        // Match anything which looks like
        // decimal degrees coordinate pair.
        const matches = query.match(
            /^[ ]*(?:Lat: )?(-?\d+\.?\d*)[, ]+(?:Lng: )?(-?\d+\.?\d*)[ ]*$/i
        );
        if (!matches) {
          return null;
        }

        function coordinateFeature(lng, lat) {
          return {
            center: [lng, lat],
            geometry: {
              type: 'Point',
              coordinates: [lng, lat]
            },
            place_name: 'Lat: ' + lat + ' Lng: ' + lng,
            place_type: ['coordinate'],
            properties: {},
            type: 'Feature'
          };
        }

        const coord1 = Number(matches[1]);
        const coord2 = Number(matches[2]);
        const geocodes = [];

        if (coord1 < -90 || coord1 > 90) {
          // must be lng, lat
          geocodes.push(coordinateFeature(coord1, coord2));
        }

        if (coord2 < -90 || coord2 > 90) {
          // must be lat, lng
          geocodes.push(coordinateFeature(coord2, coord1));
        }

        if (geocodes.length === 0) {
          // else could be either lng, lat or lat, lng
          geocodes.push(coordinateFeature(coord1, coord2));
          geocodes.push(coordinateFeature(coord2, coord1));
        }

        return geocodes;
      };

      let geocoder = new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        localGeocoder: coordinatesGeocoder,
        zoom: 10,
        placeholder: 'Address or Coordinates',
        mapboxgl: mapboxgl,
        reverseGeocode: true,
        countries: 'usa'
      })

      this.map.addControl(geocoder);

      if (this.farmAddress != null) {
        try {
          geocoder.query(this.farmAddress + ', ' + this.zipCode)
        } catch (e) {
          geocoder.query(this.zipCode)
        }
      } else if (this.zipCode) {
        geocoder.query(this.zipCode)
      }

      // Add zoom and rotation controls to the map.
      this.map.addControl(new mapboxgl.NavigationControl());
    },

    async generateDrawable() {
      let geometries = [];
      const options = {method: 'GET', headers: {accept: 'application/json'}};

      for (const feature of this.selected) {
        let response = await fetch('https://api.digifarm.io/v1/delineated-fields/high-res/' +
            feature.properties.id +
            '?token=' + this.digifarmToken,
            options);
        let json = await response.json();
        var simplifyOptions = {tolerance: .00001, highQuality: true};

        let boundary = (turf.simplify(json.geometry, simplifyOptions))
        // Some fields are retrieved as multi instead of single polygons
        if (boundary.type === "MultiPolygon") {
          geometries.push(boundary.coordinates[0])
        } else {
          geometries.push(boundary.coordinates)
        }
      }
      if(geometries.length > 0) {
        this.geography = {
          "type": "MultiPolygon",
          "coordinates": JSON.parse(JSON.stringify(geometries)),
        }
      }

      this.broadcastUpdate();

      return true;
    },

    switchMap() {
      this.broadcastUpdate();
    },

    /**
     * Add geography layer on the map.
     */
    addGeography(geography) {
      this.sourceId = 'boundaries'

      this.map.addSource(this.sourceId, {
        'type': 'geojson',
        'data': geography,
        'generateId': true,
      });

      this.map.addLayer({
        'id': this.sourceId,
        'type': 'line', // options :'fill', "line", "symbol", "circle", "heatmap", "fill-extrusion", "raster", "hillshade", "background", "sky"... SET paint accordingly
        'source': this.sourceId,
        'paint': {
          // 'fill-color': '#008ED3',
          // 'fill-opacity': 0.5
          'line-color': '#008ED3',
          'line-width': 2
        }
      });

      this.map.addLayer({
        'id': this.sourceId + '-fill',
        'type': 'fill', // options :'fill', "line", "symbol", "circle", "heatmap", "fill-extrusion", "raster", "hillshade", "background", "sky"... SET paint accordingly
        'source': this.sourceId,
        'paint': {
          'fill-color': '#008ED3',
          'fill-opacity': 0.1
        }
      });

      this.map.addLayer({
        'id': this.sourceId + '-selected',
        'type': 'fill',
        'source': this.sourceId,
        'layout': {},
        'paint': {
          'fill-color': '#fbec5d',
          'fill-opacity': [
            'case',
            ['boolean', ['feature-state', 'hover'], false],
            .5,
            0
          ]
        }
      });

      if (!this.listeners) {
        this.map.on('mousemove', this.sourceId + '-fill', (e) => {
          if (e.features.length > 0) {
            if (this.hoveredFeature !== null && !this.selected.some(item => item.properties.id === this.hoveredFeature.properties.id)) {
              this.setHighlight(this.hoveredFeature.id, false)
            }
            this.hoveredFeature = e.features[0];
            this.setHighlight(this.hoveredFeature.id, true)
          }
        });

        this.map.on('mouseleave', this.sourceId + '-fill', () => {
          if (this.hoveredFeature !== null && !this.selected.some(item => item.properties.id === this.hoveredFeature.properties.id)) {
            this.setHighlight(this.hoveredFeature.id, false)
          }
          this.hoveredFeature = null;
        });

        this.map.on('click', this.sourceId + '-fill', (e) => {
          let clickedFeature = e.features[0]
          if (!this.selected.some(item => item.properties.id === clickedFeature.properties.id)) {
            let selectedPush = this.boundaries.features[this.boundaries.features.findIndex(item => item.properties.id === clickedFeature.properties.id)]
            selectedPush.id = clickedFeature.id
            this.selected.push(selectedPush)
            this.setHighlight(clickedFeature.id, true)
          } else {
            const index = this.selected.findIndex(item => item.properties.id === clickedFeature.properties.id);
            if (index > -1) {
              this.selected.splice(index, 1);
            }
            this.setHighlight(clickedFeature.id, false)
          }
        });
        this.listeners = true;
      }
    },

    /**
     * Remove all geography layers on the map.
     */
    clearMap() {
      if (this.sourceId !== null) {
        if (this.map.isSourceLoaded(this.sourceId)) {
          this.map.removeLayer(this.sourceId)
          this.map.removeLayer(this.sourceId + '-fill')
          this.map.removeLayer(this.sourceId + '-selected')
          this.map.removeSource(this.sourceId)
          this.sourceId = null
        }
      }
    },

    /**
     * Sets the bounds of a map to fit the given geography.
     */
    setMapBoundsToFit(geography) {
      this.fullBounds = new mapboxgl.LngLatBounds();
      let bbox = turf.bbox(geography);

      this.fullBounds.extend([bbox[0], bbox[1]]);
      this.fullBounds.extend([bbox[2], bbox[3]]);

      this.map.fitBounds(this.fullBounds, {
        padding: 10,
        duration: 0,
      });
    },

    /**
     * broadcasts the current data.
     */
    broadcastUpdate() {
      this.$emit('updateGeography', {
        'geography': this.geography,
        'center': this.map.getCenter(),
        'type': 'mapboxselect',
      });
    },

    /**
     * get viewport bounding box
     */
    getBoundaries() {
      const canvas = this.map.getCanvas()
      const w = canvas.width
      const h = canvas.height
      const cUR = this.map.unproject([w * 0.9, 0]).toArray()
      const cLL = this.map.unproject([0, h * 0.9]).toArray()

      const options = {method: 'GET', headers: {accept: 'application/json'}};

      fetch('https://api.digifarm.io/v1/delineated-fields/low-res?' +
          'token=' + this.digifarmToken +
          '&bbox=' + cLL + ', ' + cUR +
          '&billing=by_field' + // Options: by_field or by_haa (area of bbox)
          '&data_version=latest', // As of July 2023, only "latest" is supported.
          options)
          .then(response => response.json())
          .then((response) => {
            this.boundaries = [];
            this.boundaries = response

            this.clearMap()
            this.addGeography(this.boundaries);

            this.selected.forEach((element) => {
              this.setHighlight(element.id, true)
            })
      })
    },

    /**
     * toggle feature  highlight
     */
    setHighlight(id, value) {
      this.map.setFeatureState(
          {source: this.sourceId, id: id},
          {hover: value}
      );
    }
  },
};

</script>

<template>
  <div class="w-full text-black">
    <div :id="this.mapId ?? this.identifier" class="h-192 w-full">
      <div v-if="this.showZoomTooltip" class="bg-white absolute inline z-50 text-xl rounded-br p-1 border-b border-r">
        Zoom in to show selectable boundaries
      </div>
    </div>
  </div>
</template>
